import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { MatTableDataSource } from "@angular/material/table";
import { MatDialog } from "@angular/material/dialog";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import {
  Colleague,
  Project,
  ProjectStatus,
  VoiceType,
} from "@models/textToSpeech/project.model";
import { ProjectService } from "~/app/textToSpeech/project.service";
import { ProjectDialogConfirmComponent } from "../../project-dialog-confirm/project-dialog-confirm.component";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ConnectWebSocketService } from "../../connectWebSocket.service";
import { catchError, debounceTime } from "rxjs/operators";
import * as moment from "moment-timezone";
import { environment } from "~/environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { getLanguageRegionList } from "~/app/shared/languageRegion";
import { ManagementService } from "~/app/management/management.service";
import { Client } from "@models/template/template.model";
import { KeyValuePair } from "@models/textToSpeech/textToSpeech.model";
import { UserHelperService } from "~/app/shared/userHelper.service";
import { NavigationExtras, Router } from "@angular/router";
import { SuiteCaseComponent } from "~/app/shared/components/suite-case/suite-case.component";
import { Pagination } from "@models/shared/action-list.model";
import { CatalogAssetSelectionService } from "~/app/shared/catalog-assets-selection.service";
import { DialogConfirmShareProjectComponent } from "~/app/shared/components/dialog-confirm-share-project/dialog-confirm-share-project.component";
import { throwError } from "rxjs";
import * as XLSX from "xlsx-js-style";

@Component({
  selector: "app-projects",
  templateUrl: "./projects.component.html",
  styleUrls: ["./projects.component.scss"],
  animations: [
    trigger("detailExpand", [
      state("collapsed", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "100px" })),
      transition(
        "expanded <=> collapsed",
        animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")
      ),
    ]),
  ],
})
export class ProjectsComponent
  extends SuiteCaseComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild("languageSelect", { static: false }) languageSelect: MatSelect;
  public selectedValue: string;
  public dataSource = new MatTableDataSource<Project>();
  @ViewChild("footerCard", { static: true, read: ElementRef }) footerCard: any;
  public dataSourceBackup: Project[];
  public columnsToDisplay = ["name"];
  public expandedElement: Project | null;
  public languageRegionList: any[] = [];
  public searchValue: string;
  public spinner: boolean;
  public allLanguageSelected = true;
  public searchGroup: UntypedFormGroup;
  public projectDeletionList: Project[] = [];
  public deletionListOpened = false;
  public defaultSortValue = environment.project.defaultProjectSorting;
  public defaultCreatedDate = environment.project.defaultCreatedTimeFilter;
  private isFirstLoad = true;
  public clients: Client[] = [];
  public colleagues: Colleague[] = [];
  public colleaguesBackup: Colleague[] = [];
  public defaultCreatedBy: number = null;
  public defaultProgress: string = null;
  public defaultVoice: string = null;
  private projectSettingsKey = "projectFilterSettings";
  public isFilesDownloadable: boolean;
  public showAdvancedFilter = false;
  public allVoices: string[] = [];
  public allStatus: string[] = [];
  public clearSearchDisabled = true;
  public completedStatus = ProjectStatus.ReadyToDownload;
  public voiceType: VoiceType;

  public pageSizeOptions = [25, 50, 100, 200];
  public totalRecords = 0;
  public totalPages = 0;
  public paginationInfo = {
    from: 0,
    startIndex: 0,
    endIndex: 0,
    pageSize: this.pageSizeOptions[0],
    isBackDisabled: true,
    isFirstDisabled: true,
    isEndDisabled: true,
    isNextDisabled: true,
  } as Pagination;

  public sortOptions: KeyValuePair[] = [
    { value: "creationDateTime", key: "dashboard.sortBy.lastCreated" },
    { value: "lastModificationDateTime", key: "dashboard.sortBy.lastModified" },
  ];

  public createdDateOptions: KeyValuePair[] = [
    { value: "all", key: "dashboard.creationDate.all" },
    { value: "24", key: "dashboard.creationDate.last24H" },
    { value: "48", key: "dashboard.creationDate.last48H" },
    { value: "24m", key: "dashboard.modifiedDate.last24H" },
    { value: "48m", key: "dashboard.modifiedDate.last48H" },
  ];
  public selectedReportType = "monthly";

  constructor(
    private dialog: MatDialog,
    private projectService: ProjectService,
    private formBuilder: UntypedFormBuilder,
    private connectWebSocketService: ConnectWebSocketService,
    private translateService: TranslateService,
    private managementService: ManagementService,
    private router: Router,
    private renderer: Renderer2,
    private assetSelectionService: CatalogAssetSelectionService
  ) {
    super();
    this.createSearchParameters();
  }

  createSearchParameters() {
    this.searchGroup = this.formBuilder.group({
      searchString: [""],
      language: [],
      statusWaitingForTTML: [false],
      statusRetake: [false],
      statusError: [false],
    });
  }

  ngOnInit() {
    this.languageRegionList = getLanguageRegionList();
    this.getColleagues();
    this.getByUserId();
    this.handleSocket();
    this.searchGroup
      .get("searchString")
      .valueChanges.pipe(debounceTime(500))
      .subscribe((x) => {
        if (this.dataSourceBackup) {
          this.filterProjects();
        }
      });
  }

  handleSocket() {
    this.connectWebSocketService.connectToProjectSocket();
    this.connectWebSocketService.socketMessages.subscribe(
      (updatedProject: string) => {
        if (!this.dataSource.data) {
          return;
        }
        const statusUpdatedProject = JSON.parse(updatedProject as string);
        const index = this.dataSource.data.findIndex(
          (project) =>
            project.uniqueProjectName === statusUpdatedProject.uniqueProjectName
        );
        if (index > -1) {
          if (
            !!statusUpdatedProject.editingBy ||
            statusUpdatedProject.editingBy === null
          ) {
            this.dataSource.data[index].editingBy =
              statusUpdatedProject.editingBy;
            return;
          }
          this.dataSource.data[index].status = statusUpdatedProject.status;
          this.dataSource.data[index].audioFilePath =
            statusUpdatedProject.audioFilePath;
          this.dataSource.data[index].vdFilePath =
            statusUpdatedProject.vdFilePath;
        }
      }
    );
  }

  filterProjects(isFilterByCreatedDateAll?: boolean) {
    // Search Filter
    const searchString = this.searchGroup.get("searchString").value;
    if (searchString && searchString.trim() !== "") {
      this.clearSearchDisabled = false;
      this.currentPageRecords = this.dataSourceBackup.filter((e) =>
        e.name.toLowerCase().includes(searchString.toLowerCase())
      );
    } else {
      this.currentPageRecords = this.dataSourceBackup.slice();
    }

    // Language Filter
    const language = this.searchGroup.get("language").value;
    if (language && language.length > 0) {
      this.currentPageRecords = this.currentPageRecords.filter((data) =>
        language.includes(data.language + "-" + data.region)
      );
    }

    // Created By Filter
    if (this.defaultCreatedBy) {
      this.currentPageRecords = this.currentPageRecords.filter(
        (data) => data.userId === this.defaultCreatedBy
      );
    }

    // Progress Filter
    const allString = this.translateService.instant(`dashboard.all`);
    const selectedProgress =
      this.defaultProgress === "DV created"
        ? "VD created"
        : this.defaultProgress;
    if (selectedProgress && selectedProgress !== allString) {
      this.currentPageRecords = this.currentPageRecords.filter(
        (data) => data.status === selectedProgress
      );
    }

    // Voice Filter
    if (this.defaultVoice && this.defaultVoice !== allString) {
      this.currentPageRecords = this.currentPageRecords.filter(
        (data) => data.voice === this.defaultVoice
      );
    }

    // Status Filter
    const ttmlStatusFilter = this.searchGroup.get("statusWaitingForTTML").value;
    const retakesStatusFilter = this.searchGroup.get("statusRetake").value;
    const errorStatusFilter = this.searchGroup.get("statusError").value;
    if (ttmlStatusFilter || retakesStatusFilter || errorStatusFilter) {
      const filterResponse: Project[] = [];
      this.currentPageRecords.forEach((data) => {
        const isPushed = false;
        if (ttmlStatusFilter && !isPushed) {
          if (
            data.status
              .toLowerCase()
              .includes(ProjectStatus.WaitForTTML.toLowerCase())
          ) {
            filterResponse.push(data);
          }
        }
        if (retakesStatusFilter && !isPushed) {
          if (data.status.toLowerCase().includes("retakes")) {
            filterResponse.push(data);
          }
        }
        if (errorStatusFilter && !isPushed) {
          if (data.errorMessage && data.errorMessage.trim() !== "") {
            filterResponse.push(data);
          }
        }
      });
      this.currentPageRecords = filterResponse;
    }

    // Created Date Filter
    if (!isFilterByCreatedDateAll) {
      this.filterByCreationDate();
    }

    this.resetPagination();
    this.initDataSource_Data(this.currentPageRecords, this.defaultIndexFrom);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      from: 0,
      startIndex: 0,
      endIndex: 0,
      pageSize: this.paginationInfo.pageSize,
      isBackDisabled: true,
      isEndDisabled: true,
      isFirstDisabled: true,
      isNextDisabled: true,
    };
  }

  getByUserId() {
    this.spinner = true;

    this.projectService.getByUserId().subscribe(
      (data) => {
        const allString = this.translateService.instant(`dashboard.all`);
        this.allVoices.push(allString);
        this.allStatus.push(allString);
        if (data) {
          data.forEach((project) => {
            const languageNameInFull = environment.project.languages.list.find(
              (l) => l.value === project.lexiconLanguage
            );
            project.voice = project.voice.split("/")[1];
            project.lexiconLanguage = languageNameInFull
              ? languageNameInFull.key
              : null;
            this.projectDeletionList.forEach((projectToDelete) => {
              if (
                projectToDelete.uniqueProjectName === project.uniqueProjectName
              ) {
                project.isSelected = true;
              }
            });
            this.allVoices.push(project.voice);
            const statusToExlude = [
              ProjectStatus.RetakesToPerform,
              ProjectStatus.WaitForTTML,
            ];
            if (
              !statusToExlude.some((status) =>
                project.status.includes(status)
              ) &&
              project.status !== ""
            ) {
              this.allStatus.push(project.status);
            }
          });
          this.allVoices = Array.from(new Set(this.allVoices));
          this.allStatus = Array.from(new Set(this.allStatus)).map((status) =>
            status === "VD created" ? "DV created" : status
          );
          this.dataSourceBackup = data;
          this.filterProjects();
        }
      },
      () => {
        this.spinner = false;
      },
      () => {
        if (this.isFirstLoad) {
          this.searchGroup.patchValue({
            language: [
              ...this.languageRegionList.map(
                (languageRegion) => languageRegion.languageRegionValue
              ),
            ],
          });
          this.isFirstLoad = false;
        } else {
          this.filterProjects();
        }
        this.spinner = false;
        this.restoreSearch();
      }
    );
  }

  clearSearch() {
    this.languageRegionList.forEach((l) => (l.isChecked = false));
    this.allLanguageSelected = true;
    this.defaultCreatedDate = "all";
    this.defaultProgress = null;
    this.defaultVoice = null;
    this.searchGroup.patchValue({
      searchString: "",
      language: [
        ...this.languageRegionList.map(
          (languageRegion) => languageRegion.languageRegionValue
        ),
      ],
      statusWaitingForTTML: false,
      statusRetake: false,
      statusError: false,
    });
    this.defaultCreatedBy = null;
    sessionStorage.clear();
    this.filterProjects();
  }

  clearSearchString() {
    this.clearSearchDisabled = true;
    this.searchGroup.patchValue({
      searchString: "",
    });
    sessionStorage.clear();
    this.saveSearchState();
    this.filterProjects();
  }

  deleteConfirmDialog(isBatchDelete: boolean, project?: Project): void {
    let batchDelete: any;
    if (isBatchDelete) {
      const details = this.projectDeletionList.map((pro) => {
        return { uniqueName: pro.uniqueProjectName, name: pro.name };
      });
      batchDelete = {
        names: details.map((detail) => detail.name).join(","),
        uniqueProjectName: details.map((detail) => detail.uniqueName).join(","),
      };
    }
    const dialogRef = this.dialog.open(ProjectDialogConfirmComponent, {
      width: "775px",
      height: "575px",
      data: {
        projectData: isBatchDelete
          ? { ...batchDelete, isBatchDelete: isBatchDelete }
          : project,
      },
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.getByUserId();
        this.projectDeletionList = [];
        this.deletionListOpened = false;
      }
    });
  }

  public download(url: string) {
    window.open(url, "_blank");
  }

  IsSpaceBarPressed(e: any) {
    if (e.keyCode === 32) {
      this.clearSearch();
    }
  }

  updateEditingBy(uniqueProjectName: string, editingBy: string) {
    if (!editingBy) {
      this.projectService
        .updateEditingBy(uniqueProjectName, "edit")
        .subscribe();
    }
  }

  languageRegionOptionClick() {
    let newStatus = true;
    this.languageSelect.options.forEach((item: MatOption) => {
      if (!item.selected) {
        newStatus = false;
      }
    });
    this.allLanguageSelected = newStatus;
  }

  showProjectDeletionList() {
    if (this.projectDeletionList.length) {
      this.dataSource.data = this.projectDeletionList;
      this.deletionListOpened = !this.deletionListOpened;
    }
  }

  hideProjectDeletionList() {
    this.deletionListOpened = !this.deletionListOpened;
    this.getByUserId();
  }

  projectSelection(project: Project) {
    if (!project.isSelected) {
      if (
        this.projectDeletionList.length <
        environment.project.batchDeletionAllowed
      ) {
        this.projectDeletionList.push(project);
        project.isSelected = !project.isSelected;
      } else {
        alert(
          this.translateService.instant(`dashboard.batchDeletionAllowedWarning`)
        );
      }
    } else {
      // remove project from deletion list
      const index = this.projectDeletionList.findIndex(
        (p) => p.uniqueProjectName === project.uniqueProjectName
      );
      this.projectDeletionList.splice(index, 1);
      if (this.deletionListOpened) {
        this.dataSource.data = this.projectDeletionList;
      }
      if (this.projectDeletionList.length === 0 && this.deletionListOpened) {
        this.deletionListOpened = !this.deletionListOpened;
        this.dataSource.data = undefined;
        this.getByUserId();
      }
      project.isSelected = !project.isSelected;
    }

    this.isFilesDownloadable = this.projectDeletionList.some(
      (proj: Project) => !!proj.audioFilePath
    );
  }

  batchDelete() {
    if (this.projectDeletionList.length) {
      // true indicates batch delete operation
      this.deleteConfirmDialog(true);
    }
  }

  bulkDownload() {
    this.projectDeletionList.forEach((project: Project) => {
      if (project.audioFilePath) {
        this.download(project.audioFilePath);
      }
    });
  }

  sortProjects(isCalledFromUI: boolean = false) {
    if (this.defaultSortValue === environment.project.defaultProjectSorting) {
      this.currentPageRecords = this.currentPageRecords
        .sortBy("creationDateTime")
        .reverse();
    } else {
      this.currentPageRecords = this.currentPageRecords
        .sortBy("lastModificationDateTime")
        .reverse();
    }

    if (isCalledFromUI) {
      this.initDataSource_Data(this.currentPageRecords, this.defaultIndexFrom);
    }
  }

  filterByCreationDate() {
    if (this.defaultCreatedDate === "all") {
      this.filterProjects(true);
    } else if (this.defaultCreatedDate.includes("m")) {
      // modification filter
      const duration = this.defaultCreatedDate.substring(
        0,
        this.defaultCreatedDate.length - 1
      );
      const modifiedDateFilter = this.currentPageRecords.filter((p) => {
        return (
          moment().diff(moment(p.lastModificationDateTime), "hours") <=
          +duration
        );
      });
      this.currentPageRecords = modifiedDateFilter;
    } else {
      // will handle the creation filter
      const creationDateFilter = this.currentPageRecords.filter((p) => {
        return (
          moment().diff(moment(p.creationDateTime), "hours") <=
          +this.defaultCreatedDate
        );
      });
      this.currentPageRecords = creationDateFilter;
    }
  }

  ngOnDestroy() {
    this.connectWebSocketService.socketMessages.unsubscribe();
  }

  getColleagues() {
    this.projectService.getColleague().subscribe((data) => {
      if (data) {
        const all = {
          id: 0,
          name: this.translateService.instant(`dashboard.all`),
        };
        this.colleaguesBackup = this.colleagues = data.sortBy("name");
        this.colleagues.forEach((user: Colleague, i) => {
          if (user.id === UserHelperService.getUserIdFromLocalStorage()) {
            user.name = "Me";
            this.colleagues.splice(i, 1);
            this.colleagues.unshift(user);
          }
        });
        data.splice(0, 0, all);
      }
    });
  }

  isLexiconAvailable(lexiconName?: string) {
    return lexiconName && lexiconName !== "";
  }

  public navigateToProjectDetails(projectId: number) {
    this.saveSearchState();
    this.clearSelectedAssetFromStorage();
    const navExtra: NavigationExtras = {
      state: { tabIndex: 1 },
    };
    this.router.navigate([`projects/edition/${projectId}`], navExtra);
  }

  public navigateToProjectPage(projectId: number, pageId: number) {
    this.saveSearchState();
    this.clearSelectedAssetFromStorage();
    const navExtra: NavigationExtras = {
      state: { pageId: pageId },
    };
    this.router.navigate([`projects/edition/${projectId}`], navExtra);
  }

  public navigateToProjectRetakes(projectId: number) {
    this.saveSearchState();
    this.clearSelectedAssetFromStorage();
    this.router.navigate([`projects/edition/${projectId}`]);
  }

  navigateToProjectCreation() {
    this.saveSearchState();
    this.clearSelectedAssetFromStorage();
    this.router.navigate([`projects/creation`]);
  }

  private clearSelectedAssetFromStorage(): void {
    sessionStorage.removeItem("draftProjectData");
    this.projectService.onChangeFileUploadContexts([]);
    this.assetSelectionService.removeSelection();
  }

  protected saveSearchState(): void {
    const lastFilter = this.searchGroup.value;
    const pageSettings = JSON.stringify({
      filter: lastFilter,
      orderBy: this.defaultSortValue,
      createdDateTime: this.defaultCreatedDate,
      progress: this.defaultProgress,
      voice: this.defaultVoice,
      createdBy: this.defaultCreatedBy,
    });
    sessionStorage.setItem(this.projectSettingsKey, pageSettings);
  }

  restoreSearch() {
    const settings = sessionStorage[this.projectSettingsKey];
    if (settings) {
      const settingsToRestore = JSON.parse(
        sessionStorage[this.projectSettingsKey]
      );
      this.searchGroup.patchValue(settingsToRestore.filter);
      this.defaultProgress = settingsToRestore.progress;
      this.defaultVoice = settingsToRestore.voice;
      this.defaultCreatedBy = settingsToRestore.createdBy;
      this.defaultCreatedDate = settingsToRestore.createdDateTime;
      this.defaultSortValue = settingsToRestore.orderBy;
      this.allLanguageSelected =
        settingsToRestore.filter.language.length ===
        this.languageRegionList.length;
    }
  }

  public pageChanged(paginationInfo: Pagination): void {
    this.paginationInfo = paginationInfo;
    this.defaultPerPageOptionsValue = paginationInfo.pageSize;
    this.initDataSource_Data(this.currentPageRecords, paginationInfo.from);

    if (scrollTo) {
      this.titleResults?.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  public changeFrom(from: number, scrollTo?: boolean): void {
    this.initDataSource_Data(this.currentPageRecords, from);

    if (scrollTo) {
      this.titleResults?.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  public onChangeOfResultsPerPage() {
    this.initDataSource_Data(this.currentPageRecords, this.defaultIndexFrom);
  }

  public initDataSource_Data(projects: Project[], indexFrom: number) {
    this.currentPageRecords = projects;
    this.responseResults = this.totalRecords = projects.length || 0;
    this.indexFrom = indexFrom;
    this.sortProjects();
    this.dataSource.data =
      this.currentPageRecords.slice(
        indexFrom,
        indexFrom + this.defaultPerPageOptionsValue
      ) || undefined;

    if (this.dataSource.data.length) {
      this.totalPages = Math.ceil(
        this.totalRecords / this.paginationInfo.pageSize
      );

      if (
        (this.paginationInfo.startIndex || 0) + this.dataSource.data.length <
        this.totalRecords
      ) {
        this.paginationInfo.isEndDisabled = false;
        this.paginationInfo.isNextDisabled = false;
      } else {
        this.paginationInfo.isEndDisabled = true;
        this.paginationInfo.isNextDisabled = true;
      }

      this.paginationInfo.startIndex =
        this.paginationInfo.startIndex === 0
          ? 1
          : this.paginationInfo.startIndex;
      this.paginationInfo.endIndex =
        this.totalRecords < this.paginationInfo.pageSize
          ? this.totalRecords
          : this.paginationInfo.endIndex === 0
          ? this.paginationInfo.pageSize
          : this.paginationInfo.endIndex;
    }
  }

  ngAfterViewChecked() {
    if (this.footerCard) {
      this.setFooterPosition(this.footerCard, this.renderer);
    }
  }

  public setFooterPosition(footCard: any, renderer: Renderer2) {
    const maxScroll =
      document.documentElement.scrollHeight -
      document.documentElement.offsetHeight;
    const currentScroll = document.documentElement.scrollTop;
    const cdkOverlay = document.querySelectorAll(
      ".cdk-overlay-backdrop, .cdk-overlay-backdrop-showing"
    );
    const matSelectOrMenu = document.querySelectorAll(
      ".mat-select-panel-wrap, .mat-menu-panel"
    );

    const footerHeight =
      cdkOverlay.length === 0 ||
      matSelectOrMenu.length !== 0 ||
      cdkOverlay.length
        ? 65
        : 0;
    renderer.setStyle(
      footCard.nativeElement.firstElementChild,
      "bottom",
      Math.max(0, footerHeight - (maxScroll - currentScroll)) + "px"
    );

    if (footerHeight === footerHeight - (maxScroll - currentScroll)) {
      renderer.setStyle(
        footCard.nativeElement.firstElementChild,
        "bottom",
        Math.max(65) + "px"
      );
    } else if (footerHeight < footerHeight - (maxScroll - currentScroll)) {
      renderer.setStyle(
        footCard.nativeElement.firstElementChild,
        "bottom",
        Math.max(0) + "px"
      );
    }
  }

  public getStatus(element: any): string {
    if (element.status.toLowerCase().includes("retakes")) {
      return element.vdFilePath
        ? "replies.dvCreated"
        : "dashboard.scriptCreated";
    }
    if (
      element.status
        .toLowerCase()
        .includes(ProjectStatus.ReadyToDownload.toLowerCase())
    ) {
      return "dashboard.completed";
    }
    if (
      element.status.toLowerCase().includes(ProjectStatus.Mixing.toLowerCase())
    ) {
      return "dashboard.mixInProgress";
    }
    return element.status === ProjectStatus.VdCreated
      ? "replies.dvCreated"
      : element.status;
  }

  public getClass(status: string): string {
    if (
      status.toLowerCase().includes(ProjectStatus.ReadyToDownload.toLowerCase())
    ) {
      return "text-success";
    }
    return "in-progress";
  }

  public getRetake(status: string): string {
    if (
      status &&
      status.toLowerCase().includes("retakes") &&
      !(status.toLowerCase() === "0 retakes to perform")
    ) {
      return status.split(" ")[0];
    }
    return null;
  }

  public onAdvanceFilterClick(): void {
    this.showAdvancedFilter = !this.showAdvancedFilter;
  }

  public isAllowDownloadingSyntheticOrVoiceRecording(
    project: Project
  ): boolean {
    const hasVdAndStereoFilePaths =
      project.vdFilePath && project.stereoFilePath;
    const isVoicer = project.voiceSetting === VoiceType.Voicer;
    const isSynthetic =
      project.voiceSetting === VoiceType.Synthetic && !!project.vdFilePath;
    return (hasVdAndStereoFilePaths && isVoicer) || isSynthetic;
  }

  public showShareProjectDialog(
    uniqueProjectName: string,
    sharedWith?: number
  ): void {
    this.spinner = true;
    const getUsersObs = this.managementService.getUsers();
    getUsersObs.subscribe((users: any) => {
      this.spinner = false;
      const data = {
        title: "scriptWriting.share.title",
        subHeading: "scriptWriting.share.subHeading",
        cancel: "scriptWriting.share.cancel",
        confirm: "scriptWriting.share.confirm",
        list: users.map((user: any) => ({
          value: user.id,
          key: user.name,
        })),
        defaultValue: sharedWith ? sharedWith?.toString() : "",
      };
      this.dialog
        .open(DialogConfirmShareProjectComponent, {
          width: "550px",
          data: data,
          autoFocus: false,
        })
        .afterClosed()
        .subscribe(({ value }) => {
          if (value) {
            this.spinner = true;
            this.projectService
              .shareProjectForVoicer(+value, uniqueProjectName)
              .pipe(catchError((err: any) => throwError(err)))
              .subscribe()
              .add(() => {
                this.getByUserId();
              });
          }
        });
    });
  }

  private getReportTranslation(col: string): string {
    return this.translateService.instant(
      `dashboard.exportReport.type.${this.selectedReportType}.fields.${col}`
    );
  }

  private formatDate(dateValue: any): string {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  }

  get isDetailedReportType(): boolean {
    return this.selectedReportType == "detailed";
  }

  private isReportGenerationInProgress = false;
  public generateReport(): void {
    if (this.isReportGenerationInProgress) return;
    this.isReportGenerationInProgress = true;

    try {
      const columns = this.isDetailedReportType
        ? [
            "name",
            "status",
            "language",
            "voice",
            "region",
            "isGeneratedTtmlFile",
            "audioDuration",
            "creationDateTime",
            "createdBy",
          ]
        : ["name", "audioDuration"];

      const headerMap = this.buildHeaderMap(columns);
      const headers = Object.values(headerMap);
      const rowData = this.isDetailedReportType
        ? this.buildRowData(this.currentPageRecords, columns)
        : this.buildMonthlyUsage(this.dataSourceBackup);
      const worksheetData = [headers, ...rowData];

      const ws = this.createWorksheet(worksheetData, headers);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Projects");

      this.downloadWorkbook(wb, this.reportName);
    } finally {
      this.isReportGenerationInProgress = false;
    }
  }

  private get reportName(): string {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `Projects_difuzevox_Report_${month}${day}.xlsx`;
  }

  private buildHeaderMap(columns: string[]): Record<string, string> {
    return columns.reduce((map, col) => {
      map[col] = this.getReportTranslation(col);
      return map;
    }, {} as Record<string, string>);
  }

  private buildRowData(data: any[], columns: string[]): any[][] {
    return data.map((item: any) =>
      columns.map((col) =>
        col === "isGeneratedTtmlFile"
          ? !item[col]
            ? "Yes"
            : "No"
          : col === "creationDateTime" && item[col]
          ? this.formatDate(item[col])
          : item[col]
      )
    );
  }

  private createWorksheet(
    worksheetData: any[][],
    headers: string[]
  ): XLSX.WorkSheet {
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);
    this.applyHeaderStyle(ws, headers);
    this.setColumnWidths(ws, headers);
    return ws;
  }

  private applyHeaderStyle(ws: XLSX.WorkSheet, headers: string[]): void {
    headers.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          fill: { patternType: "solid", fgColor: { rgb: "4F81BD" } },
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        };
      }
    });
  }

  private setColumnWidths(ws: XLSX.WorkSheet, headers: string[]): void {
    ws["!cols"] = headers.map((header) => ({ wch: header.length + 10 }));
  }

  private downloadWorkbook(wb: XLSX.WorkBook, fileName: string): void {
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private buildMonthlyUsage(data: any[]): any[][] {
    const monthlyMap: Record<string, { label: string; totalSeconds: number }> =
      {};

    for (const item of data) {
      if (!item.creationDateTime || !item.audioDuration) continue;

      const date = new Date(item.creationDateTime);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-based

      const key = `${year}-${month}`; // for sorting
      const label = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });

      const seconds = this.parseDurationToSeconds(item.audioDuration);

      if (!monthlyMap[key]) {
        monthlyMap[key] = { label, totalSeconds: 0 };
      }
      monthlyMap[key].totalSeconds += seconds;
    }

    // Sort keys in descending order (current month on top)
    const sortedKeys = Object.keys(monthlyMap).sort((a, b) => {
      const [yearA, monthA] = a.split("-").map(Number);
      const [yearB, monthB] = b.split("-").map(Number);
      if (yearA !== yearB) return yearB - yearA;
      return monthB - monthA;
    });

    return sortedKeys.map((key) => {
      const { label, totalSeconds } = monthlyMap[key];
      return [label, this.formatSecondsToHourMin(totalSeconds)];
    });
  }

  private parseDurationToSeconds(duration: string): number {
    if (!duration) return 0;
    const minuteMatch = duration.match(/(\d+)\s*m/);
    const secondMatch = duration.match(/(\d+)\s*s/);
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
    const seconds = secondMatch ? parseInt(secondMatch[1], 10) : 0;
    return minutes * 60 + seconds;
  }

  private formatSecondsToHourMin(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h.toString().padStart(2, "0")}h${m.toString().padStart(2, "0")}m`;
  }
}
